import { REJECTED_SESSION_STORAGE_KEY, RejectedSessionKey } from 'constants/errorKeys';

import { isLoginRoute } from './authRoutes';

/** Everything the teardown reaches outside of storage, so a test can watch it. */
export interface RejectedSessionEffects {
	/** Empties the profile slice. */
	clearProfile: () => void;
	resetUserAccessState: () => void;
	resetOrganization: () => void;
	resetFeatureAccess: () => void;
	signout: () => Promise<unknown>;
	/** Where the app is *now*, read after the sign-out rather than before it. */
	currentPathname: () => string;
	navigateToLogin: () => void;
	/** Reports a sign-out that did not go through. */
	onSignoutError: (error: unknown) => void;
}

/**
 * Ends a session `evaluateSession` refused to serve.
 *
 * This lives beside the gate, and out of App.tsx's effect, for the reason the
 * gate does: the order is load-bearing and it is the half that regressed.
 *
 * `clearProfile()` is what makes a rejection terminal. Without it the profile
 * stayed loaded, the gate re-read the same unservable session on its next pass
 * and rejected it again, and the `push('/login')` below minted a new location
 * that sent the app back around - a live loop, not a one-off. audit-admin ran
 * one at roughly six requests a second for as long as the tab stayed open
 * (Fizzy #2188), half of them unauthenticated, because a signed-out session
 * still re-fetched with `authorization: ''`.
 *
 * Two consequences worth keeping:
 *
 * - Nothing may be conditional on `signout()` having resolved. It can reject,
 *   and it clears `auth().currentUser` either way, so the app is unauthenticated
 *   from that moment whatever the promise does. A failure is reported rather
 *   than swallowed, and the redirect still happens.
 * - The redirect is skipped when the user is already on /login, and that guard
 *   is what bounds the whole thing. Navigating to the route you are on is not
 *   free here: connected-react-router hands back a new location object, and
 *   App.tsx's session load re-runs on it, re-fetches the profile, and hands the
 *   gate the same unservable session to reject again. Push unconditionally, as
 *   this did before, and that is a closed cycle turning as fast as the network
 *   allows. Push only when the app is somewhere else, and it can turn at most
 *   once per navigation that came from outside it.
 *
 *   The path is read through `currentPathname()` at that moment, never captured
 *   up front: sign-out nulls the user, which sends App.tsx's unauthenticated
 *   redirect to /login on its own, and a pathname snapshotted before the await
 *   would still name the route the session started on - so the guard would pass
 *   and push a second, redundant history entry on top of it.
 */
export const endRejectedSession = (errorKey: RejectedSessionKey, effects: RejectedSessionEffects): Promise<void> => {
	localStorage.clear();
	sessionStorage.clear();
	// After the clear, not before it: Auth.tsx reads this key back to say why.
	localStorage.setItem(REJECTED_SESSION_STORAGE_KEY, errorKey);

	effects.clearProfile();
	effects.resetUserAccessState();
	effects.resetOrganization();
	effects.resetFeatureAccess();

	return effects
		.signout()
		.catch(effects.onSignoutError)
		.then(() => {
			if (!isLoginRoute(effects.currentPathname())) {
				effects.navigateToLogin();
			}
		});
};
